/**
 * CaracTab.jsx
 * Onglet Caractéristiques techniques pour Articles (mode saisie) et Matériels (mode surcharge).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Edit2, Check, X, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';

// ─── Valeur affichée ──────────────────────────────────────────────────────────
function ValeurDisplay({ carac, valeur, unite }) {
  if (carac.type === 'boolean') return <span>{valeur === 'true' || valeur === true ? 'Oui' : valeur === 'false' || valeur === false ? 'Non' : '—'}</span>;
  if (!valeur && valeur !== 0) return <span style={{ color: 'var(--text-disabled)' }}>—</span>;
  return <span>{valeur}{unite ? ` ${unite}` : ''}</span>;
}

// ─── Champ de saisie pour une carac ──────────────────────────────────────────
function CaracInput({ carac, valeur, unite, onChange }) {
  if (carac.type === 'boolean') {
    return (
      <select value={valeur || ''} onChange={e => onChange({ valeur: e.target.value, unite: null })}
        className="px-2 py-1.5 border rounded-lg text-sm w-full focus:outline-none"
        style={{ borderColor: 'var(--border)' }}>
        <option value="">—</option>
        <option value="true">Oui</option>
        <option value="false">Non</option>
      </select>
    );
  }
  if (carac.type === 'select') {
    return (
      <select value={valeur || ''} onChange={e => onChange({ valeur: e.target.value, unite: null })}
        className="px-2 py-1.5 border rounded-lg text-sm w-full focus:outline-none"
        style={{ borderColor: 'var(--border)' }}>
        <option value="">—</option>
        {(carac.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (carac.type === 'numeric') {
    return (
      <div className="flex gap-2">
        <input type="number" value={valeur || ''} onChange={e => onChange({ valeur: e.target.value, unite })}
          className="flex-1 px-2 py-1.5 border rounded-lg text-sm focus:outline-none"
          style={{ borderColor: 'var(--border)' }} />
        {carac.unites?.length > 0 && (
          <select value={unite || ''} onChange={e => onChange({ valeur, unite: e.target.value })}
            className="px-2 py-1.5 border rounded-lg text-sm focus:outline-none"
            style={{ borderColor: 'var(--border)' }}>
            <option value="">—</option>
            {carac.unites.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        )}
      </div>
    );
  }
  // text
  return (
    <input type="text" value={valeur || ''} onChange={e => onChange({ valeur: e.target.value, unite: null })}
      className="px-2 py-1.5 border rounded-lg text-sm w-full focus:outline-none"
      style={{ borderColor: 'var(--border)' }} />
  );
}

// ─── Onglet pour un Article ───────────────────────────────────────────────────
export function ArticleCaracTab({ articleId }) {
  const [caracs, setCaracs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!articleId) return;
    setLoading(true);
    try {
      const r = await api.getArticleCaracs(articleId);
      setCaracs(r.data.caracteristiques || []);
    } catch { setCaracs([]); }
    finally { setLoading(false); }
  }, [articleId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setDraft(caracs.map(c => ({ ...c })));
    setEditing(true);
  };

  const updateDraft = (carac_id, { valeur, unite }) => {
    setDraft(prev => prev.map(c => c.carac_id === carac_id ? { ...c, valeur, unite } : c));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.saveArticleCaracs(articleId, draft.map(c => ({
        carac_id: c.carac_id, valeur: c.valeur || null, unite: c.unite || null
      })));
      toast.success('Caractéristiques sauvegardées');
      setEditing(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Chargement...</div>;

  if (caracs.length === 0) return (
    <div className="py-10 text-center">
      <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>Aucune caractéristique</p>
      <p className="text-xs mt-1 max-w-xs mx-auto" style={{ color: 'var(--text-disabled)' }}>
        Assignez des caractéristiques à la sous-famille de cet article dans Stocks → Familles
      </p>
    </div>
  );

  const rows = editing ? draft : caracs;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          {caracs.length} caractéristique{caracs.length > 1 ? 's' : ''}
        </span>
        {!editing ? (
          <button onClick={startEdit} data-testid="edit-article-caracs-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border hover:bg-gray-50"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            <Edit2 size={12} /> Modifier
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={save} disabled={saving}
              data-testid="save-article-caracs-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--primary-gradient)' }}>
              <Check size={12} /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
            <button onClick={() => setEditing(false)}
              className="px-3 py-1.5 rounded-lg text-xs border hover:bg-gray-50"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              <X size={12} />
            </button>
          </div>
        )}
      </div>

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        {rows.map((c, i) => (
          <div key={c.carac_id} className="flex items-center gap-3 px-4 py-3"
            style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
            <span className="w-40 flex-shrink-0 text-sm font-medium" style={{ color: 'var(--text)' }}>{c.nom}</span>
            <div className="flex-1">
              {editing ? (
                <CaracInput carac={c} valeur={c.valeur} unite={c.unite}
                  onChange={(val) => updateDraft(c.carac_id, val)} />
              ) : (
                <span className="text-sm" style={{ color: 'var(--text)' }}>
                  <ValeurDisplay carac={c} valeur={c.valeur} unite={c.unite} />
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Onglet pour un Matériel ──────────────────────────────────────────────────
export function MaterielCaracTab({ materielId }) {
  const [caracs, setCaracs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!materielId) return;
    setLoading(true);
    try {
      const r = await api.getMaterielCaracs(materielId);
      setCaracs(r.data.caracteristiques || []);
    } catch { setCaracs([]); }
    finally { setLoading(false); }
  }, [materielId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setDraft(caracs.map(c => ({ ...c })));
    setEditing(true);
  };

  const updateDraft = (carac_id, { valeur, unite }) => {
    setDraft(prev => prev.map(c => c.carac_id === carac_id ? { ...c, valeur, unite, overridden: true } : c));
  };

  const resetOverride = (carac_id) => {
    setDraft(prev => prev.map(c => c.carac_id === carac_id
      ? { ...c, valeur: c.valeur_article, unite: c.unite_article, overridden: false }
      : c));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Envoyer uniquement les carac réellement surchargées
      const overrides = draft
        .filter(c => c.overridden && (c.valeur !== c.valeur_article || c.unite !== c.unite_article))
        .map(c => ({ carac_id: c.carac_id, valeur: c.valeur || null, unite: c.unite || null }));
      await api.saveMaterielCaracOverrides(materielId, overrides);
      toast.success('Surcharges sauvegardées');
      setEditing(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Chargement...</div>;

  if (caracs.length === 0) return (
    <div className="py-10 text-center">
      <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>Aucune caractéristique</p>
      <p className="text-xs mt-1 max-w-xs mx-auto" style={{ color: 'var(--text-disabled)' }}>
        Associez cet article à une sous-famille avec des caractéristiques configurées
      </p>
    </div>
  );

  const rows = editing ? draft : caracs;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          {caracs.filter(c => c.overridden).length > 0
            ? `${caracs.filter(c => c.overridden).length} surcharge${caracs.filter(c => c.overridden).length > 1 ? 's' : ''} / ${caracs.length} carac.`
            : `${caracs.length} caractéristique${caracs.length > 1 ? 's' : ''} (valeurs article)`}
        </span>
        {!editing ? (
          <button onClick={startEdit} data-testid="edit-materiel-caracs-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border hover:bg-gray-50"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            <Edit2 size={12} /> Modifier
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={save} disabled={saving}
              data-testid="save-materiel-caracs-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--primary-gradient)' }}>
              <Check size={12} /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
            <button onClick={() => setEditing(false)}
              className="px-3 py-1.5 rounded-lg text-xs border hover:bg-gray-50"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              <X size={12} />
            </button>
          </div>
        )}
      </div>

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        {rows.map((c, i) => (
          <div key={c.carac_id} className="flex items-center gap-3 px-4 py-3"
            style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none',
              background: c.overridden ? 'var(--primary-50)' : 'white' }}>
            <div className="w-40 flex-shrink-0">
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{c.nom}</span>
              {c.overridden && !editing && (
                <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-medium"
                  style={{ background: '#FEF3C7', color: '#92400E' }}>surchargé</span>
              )}
            </div>
            <div className="flex-1">
              {editing ? (
                <CaracInput carac={c} valeur={c.valeur} unite={c.unite}
                  onChange={(val) => updateDraft(c.carac_id, val)} />
              ) : (
                <span className="text-sm" style={{ color: c.overridden ? 'var(--primary-solid)' : 'var(--text)', fontWeight: c.overridden ? 600 : 400 }}>
                  <ValeurDisplay carac={c} valeur={c.valeur} unite={c.unite} />
                </span>
              )}
            </div>
            {editing && c.overridden && (
              <button onClick={() => resetOverride(c.carac_id)} title="Réinitialiser à la valeur article"
                className="p-1.5 rounded hover:bg-yellow-50 flex-shrink-0" style={{ color: '#B45309' }}>
                <RotateCcw size={12} />
              </button>
            )}
            {!editing && c.overridden && (
              <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-disabled)' }}>
                article: <ValeurDisplay carac={c} valeur={c.valeur_article} unite={c.unite_article} />
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
